import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/ThemeContext';
import StatusBadge from './StatusBadge';
import { trustedVoyageStops, currentVesselName, liveVesselName, maxVoyageDelay } from './ShipMarker';
import { stopCountryLabel } from '../../../utils/stopLabel';
import { isHubWaiting, buildHubLines, hubLabel } from '../../../utils/hubWait';
import { ChevronDown, X } from 'lucide-react';
import {
  calculateBoxes,
  DEFAULT_BOX_KG,
} from '../../../utils/groupByVarietyType';
import { toDayMs, predictedEta, timelinePct as sharedTimelinePct, formatEtaDate } from '../../../utils/shipmentSchedule';
import StopWeightSections, { weightSections, boxes82 } from './ShipmentWeightBreakdown';
import { formatCarrierDelay, delayBarColor, showCardLevelWeight } from '../../../utils/shipmentCardDisplay';
import { formatShipChangeLine, formatVesselAuditParagraphs, buildVesselBoardFlow, buildProgressBars, cargoIsDischarged, cargoIsFullyDischarged } from '../../../utils/shipChanges';

function stopBarTitle(bar, idx, { t, multi, hasDischargedStop, departure }) {
  const stopN = t('seaTracking.card.stopN', { n: idx + 1 });
  if (bar.fullyDischarged) {
    const d = t('seaTracking.status.discharged');
    return multi ? `${stopN} · ${d}` : d;
  }
  if (bar.discharged) {
    const a = t('seaTracking.status.arrivedNotDischarged');
    return multi ? `${stopN} · ${a}` : a;
  }
  if (multi && hasDischargedStop) {
    return `${stopN} · ${t('seaTracking.status.inTransit')}`;
  }
  return multi ? stopN : (departure || '');
}

function ySpread(count, height, pad = 14) {
  if (count <= 1) return [height / 2];
  const span = height - pad * 2;
  return Array.from({ length: count }, (_, i) => pad + (span * i) / (count - 1));
}

function VesselBoardFlow({ shipment, isDark }) {
  const flow = buildVesselBoardFlow(shipment);
  const boxRef = useRef(null);
  const [tip, setTip] = useState(null);
  const showTip = (e, text) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = e.clientX - box.left + 14;
    const y = e.clientY - box.top - 32;
    setTip({
      text,
      x: Math.min(Math.max(4, x), Math.max(4, box.width - 140)),
      y: Math.max(4, y),
    });
  };
  if (!flow || (!flow.feeders.length && !flow.destinations.length)) return null;
  const feeders = flow.feeders;
  const dests = flow.destinations;
  const hasFeeders = feeders.length > 0;
  const rowH = 28;
  const h = Math.max(hasFeeders ? feeders.length : 1, dests.length, 1) * rowH + 12;
  const vbW = 360;
  const leftW = hasFeeders ? 108 : 0;
  const hubX = hasFeeders ? 130 : 8;
  const hubW = 102;
  const splitX = hubX + hubW;
  const rightX = hasFeeders ? 244 : 122;
  const leftYs = ySpread(Math.max(feeders.length, 1), h);
  const rightYs = ySpread(Math.max(dests.length, 1), h);
  const hubY = h / 2;
  const line = isDark ? '#6b7280' : '#9ca3af';
  const ink = isDark ? '#e5e7eb' : '#111827';
  const mute = isDark ? '#9ca3af' : '#6b7280';
  const hubFill = isDark ? '#282a29' : '#f3f4f6';
  const delayFill = isDark ? '#7f1d1d' : '#fef2f2';
  const delayInk = isDark ? '#fca5a5' : '#b91c1c';

  return (
    <div ref={boxRef} className="relative my-3 w-full min-w-0 overflow-visible">
      <svg
        viewBox={`0 0 ${vbW} ${h}`}
        width="100%"
        height="auto"
        preserveAspectRatio="xMidYMid meet"
        className="block max-w-full"
        role="img"
        aria-label={`${feeders.map((f) => f.name).join(', ')} into ${flow.hub}, then ${dests.map((d) => `${d.port} ${d.n}`).join(', ')}`}
      >
        {hasFeeders && feeders.map((f, i) => {
          const y = leftYs[i];
          return (
            <g key={`in-${f.name}`}>
              <foreignObject x={0} y={y - 14} width={leftW} height={28}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  onMouseEnter={(e) => f.n != null && showTip(e, `${f.n} container count`)}
                  onMouseMove={(e) => f.n != null && showTip(e, `${f.n} container count`)}
                  onMouseLeave={() => setTip(null)}
                  style={{ fontSize: 11, lineHeight: 1.15, color: ink, overflow: 'hidden' }}
                >
                  <div style={{ fontWeight: 500, wordBreak: 'break-word' }}>{f.name}</div>
                  {f.n != null && <div style={{ color: mute, fontSize: 10 }}>{f.n}</div>}
                </div>
              </foreignObject>
              <path
                d={`M ${leftW + 2} ${y} C ${hubX - 10} ${y}, ${hubX - 10} ${hubY}, ${hubX} ${hubY}`}
                fill="none"
                stroke={line}
                strokeWidth="1.25"
              />
            </g>
          );
        })}
        <rect x={hubX} y={hubY - 13} width={hubW} height={26} rx="13" fill={hubFill} />
        <foreignObject x={hubX + 4} y={hubY - 11} width={hubW - 8} height={22}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: ink,
              textAlign: 'center',
              lineHeight: '22px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {flow.hub}
          </div>
        </foreignObject>
        {dests.map((d, i) => {
          const y = rightYs[i];
          const delayed = Number(d.delay) >= 1;
          const label = `${d.port} ${d.n}${delayed ? ` +${Math.round(d.delay)}d` : ''}`;
          return (
            <g key={`out-${d.port}`}>
              <path
                d={`M ${splitX} ${hubY} C ${splitX + 14} ${hubY}, ${splitX + 14} ${y}, ${rightX} ${y}`}
                fill="none"
                stroke={delayed ? delayInk : line}
                strokeWidth="1.25"
              />
              {delayed && (
                <rect x={rightX} y={y - 11} width={vbW - rightX} height={22} rx="11" fill={delayFill} />
              )}
              <foreignObject x={rightX + 6} y={y - 11} width={vbW - rightX - 8} height={22}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  onMouseEnter={(e) => showTip(e, `${d.n} container count`)}
                  onMouseMove={(e) => showTip(e, `${d.n} container count`)}
                  onMouseLeave={() => setTip(null)}
                  style={{
                    fontSize: 11,
                    lineHeight: '22px',
                    color: delayed ? delayInk : ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
      {tip && (
        <div
          className={`pointer-events-none absolute z-20 rounded px-1.5 py-0.5 text-[0.625rem] whitespace-nowrap shadow ${
            isDark ? 'bg-[#111] text-gray-100' : 'bg-[#111827] text-white'
          }`}
          style={{ left: tip.x, top: tip.y }}
        >
          {tip.text}
        </div>
      )}
    </div>
  );
}


function uniqueStopsForAccordion(stops, barPorts) {
  const seen = new Set();
  const extra = [];
  (stops || []).forEach((stop) => {
    const key = String(stop?.arrival_port || '').toUpperCase().replace(/^PORT OF |^PORT /, '').trim();
    if (!key || seen.has(key) || (barPorts && barPorts.has(key))) return;
    seen.add(key);
    extra.push(stop);
  });
  return extra;
}

const ShipmentCard = ({ shipment, onClose, containerDetailOpen = false }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  // When container detail panel is open, offset to the right of it
  // Detail panel: left-[50px] + w-[420px] + gap = ~486px
  const getDefaultX = () => {
    if (!containerDetailOpen) return 16;
    // On small screens, stack below instead of beside
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const detailWidth = vw >= 640 ? 420 : 360; // matches sm:w-[420px] / w-[360px]
    const offset = 50 + detailWidth + 16; // search left + detail panel + gap
    return offset + 320 > vw ? 16 : offset; // fallback to left if card won't fit
  };
  const [pos, setPos] = useState({ x: getDefaultX(), y: 16 });
  const [isDragging, setIsDragging] = useState(false);
  const [openStops, setOpenStops] = useState({});
  const [openCommodities, setOpenCommodities] = useState({});
  const commodityIsOpen = (key) => openCommodities[key] !== false;
  const toggleCommodity = (key) => {
    setOpenCommodities((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const cardRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Reset position when shipment changes or detail panel toggles
  useEffect(() => {
    setPos({ x: getDefaultX(), y: 16 });
  }, [shipment?.container_number, containerDetailOpen]);

  useEffect(() => {
    const current = String(shipment?.arrival_port || '').toUpperCase();
    const next = {};
    trustedVoyageStops(shipment).forEach((stop, idx) => {
      const key = String(stop?.container_number || stop?.arrival_port || `stop-${idx}`);
      next[key] = String(stop?.arrival_port || '').toUpperCase() === current;
    });
    setOpenStops(next);
  }, [shipment?.container_number, shipment?.arrival_port, shipment?.voyage_stops, shipment?.has_shipsgo_current, shipment?.vessel]);

  const handleDragStart = useCallback((clientX, clientY) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    dragOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((clientX, clientY) => {
    const card = cardRef.current;
    const parent = card?.parentElement;
    if (!card || !parent) return;
    const pRect = parent.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    let newX = clientX - pRect.left - dragOffsetRef.current.x;
    let newY = pRect.bottom - clientY - (cRect.height - dragOffsetRef.current.y);
    newX = Math.max(0, Math.min(newX, pRect.width - cRect.width));
    newY = Math.max(0, Math.min(newY, pRect.height - cRect.height));
    setPos({ x: newX, y: newY });
  }, []);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => { e.preventDefault(); handleDragMove(e.clientX, e.clientY); };
    const onTouchMove = (e) => { handleDragMove(e.touches[0].clientX, e.touches[0].clientY); };
    const onEnd = () => handleDragEnd();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!shipment) return null;

  const hasField = (v) => v === 0 || Boolean(v);
  const portCountry = (port, country) => {
    const p = String(port || '').trim();
    const c = String(country || '').trim();
    if (p && c) return `${p}, ${c}`;
    return p || c || null;
  };
  const departureLabel = portCountry(
    shipment.departure_port || shipment.pol || shipment.origin,
    shipment.origin_country,
  );
  const destinationLabel = portCountry(
    shipment.arrival_port,
    shipment.destination_country,
  );

  const voyageStops = trustedVoyageStops(shipment);
  const stopArrivalEta = (stop) => predictedEta(stop);
  const stopEtaMs = (stop) => {
    const ms = toDayMs(stopArrivalEta(stop));
    return ms == null ? Number.POSITIVE_INFINITY : ms;
  };
  const sortedStops = [...voyageStops].sort((a, b) => stopEtaMs(a) - stopEtaMs(b));
  const currentPort = String(shipment.arrival_port || '').toUpperCase();
  const legBars = buildProgressBars(shipment);
  const rawBars = legBars || (sortedStops.length > 0
    ? sortedStops.map((stop, idx, arr) => ({
        key: stop?.container_number || `${stop?.arrival_port || 'stop'}-${idx}`,
        port: stop?.arrival_port || '—',
        country: stop?.destination_country,
        leftRaw: idx === 0 ? shipment.etd : stopArrivalEta(arr[idx - 1]),
        rightRaw: stopArrivalEta(stop),
        delay_days: stop?.delay_days,
        transit_percentage: stop?.transit_percentage,
        status: stop?.status,
        discharged: cargoIsDischarged(stop),
        fullyDischarged: cargoIsFullyDischarged(stop),
      }))
    : [{
        key: 'solo',
        port: shipment.arrival_port || '—',
        leftRaw: shipment.etd,
        rightRaw: predictedEta(shipment),
        delay_days: shipment.delay_days,
        discharged: cargoIsDischarged(shipment),
        fullyDischarged: cargoIsFullyDischarged(shipment),
      }]);
  const barItems = [];
  const seenBarPorts = new Set();
  rawBars.forEach((bar) => {
    const key = String(bar.port || '').toUpperCase().replace(/^PORT OF |^PORT /, '').trim();
    if (key && seenBarPorts.has(key)) return;
    if (key) seenBarPorts.add(key);
    barItems.push(bar);
  });
  if (rawBars.fromLegs) barItems.fromLegs = true;
  const hubWaiting = isHubWaiting(shipment);
  const boardFlow = hubWaiting ? null : buildVesselBoardFlow(shipment);
  const auditParagraphs = hubWaiting || boardFlow ? [] : formatVesselAuditParagraphs(shipment);
  const shipChangeLine = hubWaiting || barItems.fromLegs || boardFlow || auditParagraphs.length
    ? null
    : formatShipChangeLine(shipment);
  const accordionStops = sortedStops.filter((s) => s?.arrival_port);
  const showStopsAccordion = accordionStops.length > 0;

  const commoditySections = weightSections(shipment);

  return (
    <div
      ref={cardRef}
      className={`absolute z-[1000] w-[calc(100%-16px)] max-w-[360px] lg:w-[320px] max-h-[min(520px,75dvh)] lg:h-[380px] lg:max-h-[calc(100vh-300px)] overflow-y-auto rounded-lg shadow-lg border p-4 ${
        isDark
          ? 'bg-[#1d1d1d] border-[#333] text-white'
          : 'bg-white border-[#e5e7eb] text-[#1f2937]'
      }`}
      style={{ left: pos.x, bottom: pos.y, paddingBottom: '16px', userSelect: isDragging ? 'none' : 'auto' }}
    >
      {/* Header — drag handle */}
      <div
        className={`flex items-start justify-between mb-1 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={(e) => handleDragStart(e.clientX, e.clientY)}
        onTouchStart={(e) => handleDragStart(e.touches[0].clientX, e.touches[0].clientY)}
      >
        <div>
          <div className="font-semibold text-sm">{shipment.commodities || 'Unknown'}</div>
          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
            {t('seaTracking.card.origin')}: {shipment.origin || '—'}
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded hover:bg-[#f3f4f6] dark:hover:bg-[#282a29]`}
        >
          <X size={16} />
        </button>
      </div>

      {hubWaiting && (() => {
        const title = hubLabel(shipment);
        const [status, ...rest] = buildHubLines(shipment);
        return (
          <div className="mb-3 text-xs">
            <div className="font-semibold text-sm text-[#B45309]">{t(title.key, title.params)}</div>
            <div className={`mb-1 ${isDark ? 'text-amber-400' : 'text-[#B45309]'}`}>{t(status.key)}</div>
            {rest.map((line) => (
              <div key={line.key} className={isDark ? 'text-gray-300' : 'text-[#4b5563]'}>{t(line.key, line.params)}</div>
            ))}
          </div>
        );
      })()}

      {/* Status badge */}
      {!hubWaiting && (
      <div className="mb-2">
        <StatusBadge
          status={shipment.status}
          delayDays={shipment.delay_days}
          etaHasUpdate={shipment.eta_has_update}
          etaUpdated={shipment.eta_updated}
          etaInitial={shipment.eta_initial}
          trackingMethod={shipment.tracking_method}
          shipment={shipment}
        />
      </div>
      )}

      {/* One progress bar per voyage stop (current/primary larger) */}
      <div className="mb-3 flex flex-col gap-[0.6rem]">
        {barItems.map((bar, idx) => {
          const port = stopCountryLabel({
            port: bar.port || '—',
            stopCountry: bar.country,
            cardPort: shipment.arrival_port,
            cardCountry: shipment.destination_country,
          }) || bar.port || '—';
          const liveIdx = barItems.findIndex((b) => !b.discharged);
          const isPrimary = (liveIdx >= 0 ? liveIdx : 0) === idx;
          const hasDischargedStop = barItems.slice(0, idx).some((b) => b.fullyDischarged);
          const pct = (() => {
            const raw = Number(bar.transit_percentage);
            if (Number.isFinite(raw)) return Math.max(0, Math.min(100, Math.round(raw)));
            if (bar.discharged) return 100;
            return sharedTimelinePct(bar.leftRaw, predictedEta({
              estimated_arrival: bar.rightRaw,
              eta: bar.rightRaw,
            }));
          })();
          const delayVal = hasField(bar.delay_days) ? bar.delay_days : null;
          const delayLabel = hasField(delayVal) ? formatCarrierDelay(delayVal) : null;
          const arrival = formatEtaDate(bar.rightRaw) || (hasField(bar.rightRaw) ? String(bar.rightRaw).slice(0, 10) : null);
          const depart = formatEtaDate(bar.leftRaw) || (hasField(bar.leftRaw) ? String(bar.leftRaw).slice(0, 10) : null);
          const color = delayBarColor(delayVal);
          const fill = pct == null ? 0 : Math.min(Math.max(pct, 0), 100);
          const barH = barItems.length > 1 ? 'h-3' : (isPrimary ? 'h-2.5' : 'h-1.5');
          const textCls = isPrimary ? 'text-xs' : 'text-[0.625rem]';
          return (
            <div key={bar.key || `${port}-${idx}`}>
              <div className={`flex justify-between items-baseline gap-2 leading-none mb-px ${textCls} ${isDark ? 'text-gray-200' : 'text-[#1f2937]'}`}>
                <span className="shrink-0 min-w-0">
                  {stopBarTitle(bar, idx, {
                    t,
                    multi: barItems.length > 1,
                    hasDischargedStop,
                    departure: shipment.departure_port || '',
                  })}
                </span>
                <span className="min-w-0 text-right break-words">{port}</span>
              </div>
              <div className="relative w-full flex items-center">
                <div className={`w-full ${barH} rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className={`${barH} rounded-full`}
                    style={{ width: `${fill}%`, backgroundColor: color }}
                  />
                </div>
                <span
                  className={`flex absolute inset-0 items-center justify-center pointer-events-none font-semibold text-xs leading-none ${Number(delayVal) >= 1 ? 'text-[#7f1d1d]' : (isDark ? 'text-gray-100' : 'text-[#14532d]')}`}
                >
                  {pct == null ? '—' : `${pct}%`}
                </span>
              </div>
              <div className={`flex justify-between gap-2 mt-0.5 text-[0.625rem] ${isDark ? 'text-gray-400' : 'text-[#4b5563]'}`}>
                <span className="shrink-0">{depart ? `${t('seaTracking.card.etd')} ${depart}` : '—'}</span>
                <span className="text-right">
                  {arrival || ''}
                  {arrival && delayLabel ? ' · ' : ''}
                  {delayLabel && (
                    <span className={Number(delayVal) >= 1 ? 'text-red-600 dark:text-red-400' : ''}>
                      {delayLabel}
                    </span>
                  )}
                </span>
              </div>

            </div>
          );
        })}
      </div>

      {!hubWaiting && <VesselBoardFlow shipment={shipment} isDark={isDark} />}

      {/* Vessel / line / delay / containers / weight */}
      <div className="space-y-1 text-xs">
        {!hubWaiting && <Row label={t('seaTracking.card.vessel')} value={currentVesselName(shipment)} isDark={isDark} />}
        {!hubWaiting && liveVesselName(shipment) && (
          <Row label={t('seaTracking.card.currentVessel')} value={liveVesselName(shipment)} isDark={isDark} />
        )}

        {!hubWaiting && hasField(shipment.booking_vessel || shipment.bookingVessel)
          && !boardFlow
          && String(shipment.booking_vessel || shipment.bookingVessel) !== String(currentVesselName(shipment))
          && String(shipment.booking_vessel || shipment.bookingVessel) !== String(liveVesselName(shipment)) && (
          <Row label={t('seaTracking.card.bookingVessel')} value={shipment.booking_vessel || shipment.bookingVessel} isDark={isDark} />
        )}
        {auditParagraphs.map((para) => (
          <div
            key={para}
            className={`text-xs italic whitespace-normal break-words ${isDark ? 'text-gray-400' : 'text-[#4b5563]'}`}
          >
            {para}
          </div>
        ))}
        {shipChangeLine && (
          <div className={`text-xs italic whitespace-normal break-words ${isDark ? 'text-gray-400' : 'text-[#4b5563]'}`}>{shipChangeLine}</div>
        )}
        <Row label={t('seaTracking.card.shippingLine')} value={shipment.shipping_companies || shipment.shippingCompanies} isDark={isDark} />
        {departureLabel && (
          <Row label={t('seaTracking.card.departure', 'Departure')} value={departureLabel} isDark={isDark} />
        )}
        {destinationLabel && (
          <Row label={t('seaTracking.card.destination')} value={destinationLabel} isDark={isDark} />
        )}
        <Row label={t('seaTracking.card.submarket')} value={shipment.submarket} isDark={isDark} />
        <Row label={t('seaTracking.card.containers')} value={shipment.total_containers} isDark={isDark} />
        {shipment.total_weight ? (
          <div className="flex justify-between text-xs">
            <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>{t('seaTracking.card.total')}</span>
            <span className="text-right ml-2 font-medium">
              {Number(shipment.total_weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg
            </span>
          </div>
        ) : null}
      </div>

      {showStopsAccordion && (
        <div className="mt-3">
          <div className={`text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-[#4b5563]'}`}>
            {t('seaTracking.card.voyageStops', 'Voyage stops')}
          </div>
          {accordionStops.map((stop, idx) => {
            const port = stop?.arrival_port || '';
            const eta = stopArrivalEta(stop);
            const arrival = formatEtaDate(eta);
            const stopSections = weightSections(stop);
            const groups = stopSections.flatMap((s) => s.groups);
            const totalWeight = Number(stop?.total_weight) || stopSections.reduce((sum, s) => sum + (Number(s.totalWeight) || 0), 0)
              || groups.reduce((sum, g) => sum + (Number(g.subtotal) || 0), 0);
            const boxCount = boxes82(totalWeight);
            const isCurrent = String(port).toUpperCase() === currentPort;
            const stopId = String(stop?.container_number || port || `stop-${idx}`);
            const expanded = openStops[stopId] ?? isCurrent;
            return (
              <div key={stopId} className={idx > 0 ? 'mt-2' : ''}>
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpenStops((prev) => ({ ...prev, [stopId]: !expanded }))}
                  className={`w-full px-2 py-1.5 text-xs font-bold text-left ${
                    expanded ? 'rounded-t' : 'rounded'
                  } ${
                    isDark ? 'bg-[#333] text-white' : 'bg-[#e5e7eb] text-[#1f2937]'
                  } ${isCurrent ? (isDark ? 'ring-1 ring-inset ring-white/40' : 'ring-1 ring-inset ring-[#1f2937]/20') : ''}`}
                >
                  <span className="flex justify-between items-center gap-2">
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <ChevronDown size={12} className={`shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                      <span className="whitespace-normal break-words">{port || '—'}</span>
                    </span>
                    {arrival && <span className="shrink-0">{arrival}</span>}
                  </span>
                  {totalWeight > 0 && (
                    <span className={`block pl-4 mt-0.5 font-bold ${isDark ? 'text-white' : 'text-[#1f2937]'}`}>
                      {boxCount.toLocaleString()} {t('seaTracking.card.boxes', 'boxes')} ({DEFAULT_BOX_KG} kg)
                      <span className="ml-1">| {totalWeight.toLocaleString()} kg</span>
                    </span>
                  )}
                </button>
                {expanded && (
                <StopWeightSections sections={stopSections} stopId={stopId} isDark={isDark} commodityIsOpen={commodityIsOpen} toggleCommodity={toggleCommodity} />
                )}
              </div>
            );
          })}
        </div>
      )}


      {/* Weight per Variety grouped by commodity then type */}
      {showCardLevelWeight({ showStopsAccordion, hasWeightGroups: commoditySections.some(s => s.groups.length > 0) }) && (
        <div className="mt-3">
          {commoditySections.map((section, sIdx) => (
            section.groups.length > 0 && (
              <div key={section.commodity || sIdx} className={sIdx > 0 ? 'mt-2' : ''}>
                {/* Commodity header (only if multi-commodity structure) */}
                {section.commodity && (() => {
                  const cKey = `card:${section.commodity}`;
                  const cOpen = commodityIsOpen(cKey);
                  return (
                  <button
                    type="button"
                    aria-expanded={cOpen}
                    onClick={() => toggleCommodity(cKey)}
                    className={`w-full flex items-center gap-x-2 min-w-0 px-2 py-1.5 text-xs font-bold text-left ${
                      cOpen ? 'rounded-t' : 'rounded'
                    } ${isDark ? 'bg-[#333] text-white' : 'bg-[#e5e7eb] text-[#1f2937]'}`}
                  >
                    <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
                      <ChevronDown size={12} className={`shrink-0 transition-transform ${cOpen ? '' : '-rotate-90'}`} />
                      <span className="min-w-0 truncate">{section.commodity}</span>
                    </span>
                    {section.totalWeight > 0 && (
                        <span className="shrink-0 text-right font-normal whitespace-nowrap">
                          {boxes82(section.totalWeight).toLocaleString()} {t('seaTracking.card.boxes', 'boxes')}
                          <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                            | {Number(section.totalWeight).toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg
                          </span>
                        </span>
                    )}
                  </button>
                  );
                })()}
                {(!section.commodity || commodityIsOpen(`card:${section.commodity}`)) && (
                <div className={`rounded border text-xs ${isDark ? 'border-[#333]' : 'border-[#e5e7eb]'} ${
                  section.commodity ? 'rounded-t-none border-t-0' : ''
                }`}>
                  {section.groups.map((group, gIdx, groups) => {
                    const showTypeRow = group.varieties.length > 1;
                    return (
                    <div key={`${group.type}-${gIdx}`}>
                      {/* Type header row — skip when one variety (avoids duplicating same totals as detail row) */}
                      {showTypeRow && (() => {
                        const typeBoxTotal = group.varieties.reduce((sum, v) => {
                          const b = calculateBoxes(v);
                          return sum + (b.equivBoxes82 || boxes82(v.weight_kg));
                        }, 0);
                        return (
                          <div
                            className={`flex justify-between px-2 py-1.5 font-semibold ${
                              isDark ? 'bg-[#282a29]' : 'bg-[#f3f4f6]'
                            } ${gIdx !== 0 ? (isDark ? 'border-t border-[#333]' : 'border-t border-[#e5e7eb]') : ''}`}
                          >
                            <span>{group.type}</span>
                            <span>
                              {typeBoxTotal > 0 ? (
                                <>
                                  <span className="font-bold">{typeBoxTotal.toLocaleString()} {t('seaTracking.card.boxes', 'boxes')} ({DEFAULT_BOX_KG} kg)</span>
                                  <span className={`ml-1 font-normal text-[0.625rem] ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                                    | {group.subtotal.toLocaleString()} kg
                                  </span>
                                </>
                              ) : (
                                <>{group.subtotal.toLocaleString()} kg</>
                              )}
                            </span>
                          </div>
                        );
                      })()}
                      {/* Variety rows */}
                      {group.varieties.map((item, idx) => {
                        const boxInfo = calculateBoxes(item);
                        return (
                          <div
                            key={idx}
                            className={`flex justify-between pr-2 py-1 ${
                              showTypeRow ? 'pl-5' : 'pl-2'
                            } ${
                              idx !== group.varieties.length - 1 || gIdx !== groups.length - 1
                                ? isDark ? 'border-b border-[#333]' : 'border-b border-[#e5e7eb]'
                                : ''
                            } ${!showTypeRow && gIdx !== 0 ? (isDark ? 'border-t border-[#333]' : 'border-t border-[#e5e7eb]') : ''}`}
                          >
                            <span className={`min-w-0 break-words pr-2 ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                              {item.variety}
                            </span>
                            <span className="shrink-0 text-right">
                              {(boxInfo.equivBoxes82 || boxes82(item.weight_kg)) ? (
                                <>
                                  <span className="font-bold">{(boxInfo.equivBoxes82 || boxes82(item.weight_kg)).toLocaleString()} × {DEFAULT_BOX_KG} kg</span>
                                  <span className={`block font-normal text-[0.625rem] ${isDark ? 'text-gray-400' : 'text-[#828382]'}`}>
                                    {(Number(item.weight_kg) || 0).toLocaleString()} kg
                                  </span>
                                </>
                              ) : (
                                <span className="font-medium">{(Number(item.weight_kg) || 0).toLocaleString()} kg</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                  })}
                </div>
                )}
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, isDark }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between">
      <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>{label}</span>
      <span className="font-medium text-right ml-2 min-w-0 max-w-[180px] whitespace-normal break-words">{value}</span>
    </div>
  );
};

export default ShipmentCard;
